<!--
Structural copy of ../so-service-priorities/CloneToFranchiseModal.vue —
collects the target value and count, emits `apply` for the parent table to
execute (the parent owns the sequential POST loop + progress bar, since it
also owns the rows needed to name a failed item by name).
-->
<template>
  <modal
    elem-id="modal_so-catalog-fiscal-bulk"
    data-testid="so-catalog-fiscal-bulk-modal"
    :show="isVisible"
    :title="modalTitle"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="t('Aplicar')"
    :save-fn="handleApply"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <p class="bulk-info">
        {{ t('Aplicar') }} <strong>{{ code || '—' }}</strong> {{ t('a') }}
        <strong>{{ selectedCount }}</strong> {{ itemsLabel }}
        — {{ t('escopo:') }} <strong>{{ scopeLabel }}</strong>
      </p>

      <div class="form-field">
        <input-text
          :elem-id="`soCatalogFiscalBulk${kind}`"
          v-model="code"
          :label="meta.fieldLabel"
          :placeholder="meta.placeholder"
          :validation="validateCode"
          fluid
          :data-testid="meta.testId"
        />
        <small v-if="showError" class="error-message">{{ t('Campo obrigatório') }}</small>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FiscalScope } from '../../composables/useSOCatalogFiscal';
import { isValidLc116Code, isValidNcmCode } from './fiscalCodeValidation';
import { log } from '../../utils/DebugLogger';

type Id = number | string;
type Kind = 'service' | 'product' | 'additional_service';

interface Props {
  kind: Kind;
  scope: FiscalScope;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  apply: [payload: { ids: Id[]; code: string }];
  close: [];
}>();

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const code = ref<string>('');
const showError = ref<boolean>(false);
const selectedIds = ref<Id[]>([]);
const selectedCount = ref<number>(0);

interface KindMeta {
  fieldLabel: string;
  placeholder: string;
  itemsLabel: string;
  title: string;
  validate: (code: string) => boolean;
  testId: string;
}

// Per-kind copy/validator table — the 3 domains share this modal's shell but
// each has its own field name, format and data-testid. 'additional_service'
// shares the LC116 validator with 'service' (same fiscal format, see
// fiscalCodeValidation.ts) but keeps its own testId so the two tabs' bulk
// inputs are independently addressable in tests.
const KIND_META = computed<Record<Kind, KindMeta>>(() => ({
  service: {
    fieldLabel: t('Código LC116'),
    placeholder: t('Ex: 14.01'),
    itemsLabel: t('serviços'),
    title: t('Aplicar LC116 a vários serviços'),
    validate: isValidLc116Code,
    testId: 'so-catalog-fiscal-bulk-lc116',
  },
  product: {
    fieldLabel: t('NCM'),
    placeholder: t('Ex: 12345678'),
    itemsLabel: t('produtos'),
    title: t('Aplicar NCM a vários produtos'),
    validate: isValidNcmCode,
    testId: 'so-catalog-fiscal-bulk-ncm',
  },
  additional_service: {
    fieldLabel: t('Código LC116'),
    placeholder: t('Ex: 14.01'),
    itemsLabel: t('serviços adicionais'),
    title: t('Aplicar LC116 a vários serviços adicionais'),
    validate: isValidLc116Code,
    testId: 'so-catalog-fiscal-bulk-lc116-additional',
  },
}));

const meta = computed<KindMeta>(() => KIND_META.value[props.kind]);

const itemsLabel = computed<string>(() => meta.value.itemsLabel);

const scopeLabel = computed<string>(() =>
  props.scope === 'global'
    ? t('Global (franqueadora) — vale para todas as franquias')
    : t('Somente a franquia selecionada')
);

const modalTitle = computed<string>(() => meta.value.title);

const validateCode = (): boolean => meta.value.validate(code.value);

const open = (ids: Id[]): void => {
  log('SOCatalogFiscalBulkApplyModal', 'open', 'Opening with', ids.length, 'records');
  selectedIds.value = ids;
  selectedCount.value = ids.length;
  code.value = '';
  showError.value = false;
  isVisible.value = true;
};

const handleClose = (): void => {
  isVisible.value = false;
  code.value = '';
  showError.value = false;
  selectedIds.value = [];
  emit('close');
};

const handleApply = async (): Promise<void> => {
  if (!validateCode()) {
    showError.value = true;
    return;
  }
  showError.value = false;
  emit('apply', { ids: selectedIds.value, code: code.value });
  handleClose();
};

defineExpose({ open });
</script>

<style scoped>
.bulk-info {
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: var(--p-text-color);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
}
</style>
